import React from 'react'
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  Typography
} from '@mui/material';
import { AccessTimeOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import { Session, User } from '../types';
import { isSessionEnded, isUnavailable, useIsInSession } from '../isInSession';
import { useGetUserInfoQuery } from '../services/api';
import { getSessionDate, getSessionTime } from '../getSessionDateTime';

const SessionCard = ({
  index,
  lg = 6,
  md = 6,
  onClick,
  session,
  xl = 4,
}: {
  index: number
  lg?: number
  md?: number
  onClick: () => void
  session: Session
  xl?: number
}) => {
  const { data: user } = useGetUserInfoQuery()

  const inSession = useIsInSession(session);

  return (
    <Grid item
      xs={12}
      sm={6}
      md={md}
      lg={lg}
      xl={xl}
    >
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between'
        }}
      >
        <CardContent
          onClick={onClick}
          style={{
            flex: 1,
          }}
        >
          <Typography
            // gutterBottom
            style={{
              fontSize: 20,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {session.title}
          </Typography>
          <Typography
            style={{
              color: '#4fa3ad',
              fontSize: 14,
              fontWeight: 500,
              marginBottom: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {session.practitioner?.fullName}
          </Typography>
          <Grid
            container
          >
            <Grid item>
              <Box
                alignItems='center'
                display='flex'
                fontSize={13}
                marginBottom={1}
              >
                <CalendarMonthOutlined />
                <Typography
                  style={{
                    fontSize: 12,
                    marginLeft: 2,
                  }}
                >
                  {getSessionDate(session)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={1} />
            <Grid item>
              <Box
                alignItems='center'
                display='flex'
                fontSize={12}
                marginBottom={1}
              >
                <AccessTimeOutlined />
                <Typography
                  style={{
                    fontSize: 12,
                    marginLeft: 2,
                  }}
                >
                  {getSessionTime(session)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box
            display='flex'
          >
            <Typography
              style={{
                fontSize: 12,
                fontWeight: 500,
                marginRight: 4
              }}
            >
              Session Length
            </Typography>
            <Typography
              style={{
                fontSize: 12,
              }}
            >
              {session.duration} minutes
            </Typography>
          </Box>
          <Box flexDirection="row" marginTop={2}
            sx={{
              "& .MuiChip-root": {
                borderRadius: '4px'
              },
            }}
          >
            <SessionTags
              session={session}
            />
          </Box>
        </CardContent>
        <CardActions
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box
            display='flex'
            fontSize={13}
          >
            <SessionSeats
              inSession={inSession}
              session={session}
              user={user}
            />
          </Box>
          <StartOrJoin
            index={index}
            inSession={inSession}
            onClick={onClick}
            session={session}
            user={user}
          />
        </CardActions>
      </Card>
    </Grid>
  );
}

export default SessionCard;

export const SessionTags = ({
  session,
}: {
  session?: Session
}) => {
  return (<>
    {session?.tags.map(tag => (
      <React.Fragment key={tag}>
        <Chip
          label={tag}
          style={{
            fontSize: 13,
            fontWeight: 500,
            marginBottom: 2
          }}
          variant="outlined"
        />
        &nbsp;
      </React.Fragment>
    ))}
  </>);
}

export const StartOrJoin = ({
  index,
  inSession,
  onClick,
  session,
  user,
}: {
  index: number
  inSession: boolean
  onClick: () => void
  session: Session
  user?: User
}) => {
  const navigate = useNavigate()

  if (inSession)
    return (
      <Button
        data-cy="startOrJoin"
        disabled={isUnavailable(session, user?.role)}
        id={'startOrJoin' + index}
        onClick={async () => {
          navigate(`/room/${session?.guid}`)
        }}
        variant="outlined"
      >
        {user?.role === 'practitioner'
          ? 'Start'
          : 'Join'
        }
      </Button>
    );

  return (
    <Button
      onClick={onClick}
      color='secondary'
      endIcon={'>'}
    >Learn More</Button>
  );
}

export const SessionSeats = ({
  inSession,
  session,
  user,
}: {
  inSession: boolean
  session: Session
  user?: User
}) => {
  if (user?.role !== 'member' && session.startDateTime)
    return (
      <>
        <Typography
          fontWeight={500}
        >{session?.bookings}</Typography>
        &nbsp;
        <Typography>Seats Booked</Typography>
      </>
    );

  if (!inSession && !isSessionEnded(session))
    return (
      <>
        <Typography
          fontWeight={500}
        >{session?.seats}</Typography>
        &nbsp;
        <Typography>Seats Available</Typography>
      </>
    );

  return null;
}